import type { ParamValue } from '@/utils/querystring';
import { FilterContext, FilterContextType, FilterType } from '@/contexts/filter';
import { QueryTypeEnum, SearchMetadata } from '@/contexts/search';
import { filter2v1 } from '@/dataManagement/filterAdapter';
import {
  filter2predicate,
  FilterConfigType,
} from '@/dataManagement/filterAdapter/filter2predicate';
import { EventFiltering, Predicate, PredicateType } from '@/gql/graphql';
import { cn } from '@/utils/shadcn';
import { SuggestConfig, TaxonSuggestType } from '@/utils/suggestEndpoints';
import React, { useContext } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { Button } from '../ui/button';
import { AboutButton } from './aboutButton';
import { DateRangeFilter } from './dateRangeFilter';
import { EnumFilter } from './enumFilter';
import { Exists } from './exists';
import { FilterButton } from './filterButton';
import { FilterPopover } from './filterPopover';
import { GeometryFilter } from './geometryFilter';
import { GeoTimeFilter } from './geoTimeFilter';
import MoreFilters from './More';
import { SkeletonOption } from './option';
import { OptionalBooleanFilter } from './optionalBooleanFilter';
import { QFilter } from './QFilter';
import { QInlineButtonFilter } from './QInlineButtonFilter';
import { InlineToggleFilter } from './inlineToggleFilter';
import { RangeFilter } from './rangeFilter';
import { SuggestionItem, SuggestResponseType } from './suggest';
import { SuggestFilter } from './suggestFilter';
import { TaxonFilter } from './taxonFilter';
import { WildcardFilter } from './wildcardFilter';
import { HumboldtBooleansFilter } from './humboldtBooleansFilter';
import { CustomPredicateFilter } from './customPredicateFilter';
import { SequenceFilter } from './sequenceFilter/SequenceFilter';
import { useSequenceResolution } from './sequenceFilter/sequenceResolutionContext';

export enum filterConfigTypes {
  SUGGEST = 'SUGGEST',
  ENUM = 'ENUM',
  FREE_TEXT = 'FREE_TEXT',
  RANGE = 'RANGE',
  DATE_RANGE = 'DATE_RANGE',
  OPTIONAL_BOOL = 'OPTIONAL_BOOL',
  WILDCARD = 'WILDCARD',
  LOCATION = 'LOCATION',
  TAXON = 'TAXON',
  GEOLOGICAL_TIME = 'GEOLOGICAL_TIME',
  INLINE_TOGGLE = 'INLINE_TOGGLE',
  HUMBOLDT_BOOLEANS = 'HUMBOLDT_BOOLEANS',
  CUSTOM_PREDICATE = 'CUSTOM_PREDICATE',
  SEQUENCE = 'SEQUENCE',
}

export type AdditionalFilterProps = {
  searchConfig: FilterConfigType;
  onApply?: ContentOnApply;
  onCancel?: () => void;
  pristine?: boolean;
};

export type filterConfigShared = {
  filterType: string;
  filterHandle: string;
  displayName: React.FC<{ id: string | number | object; checklistKey?: string }>;
  filterTranslation: string;
  content?: React.FC;
  filterButtonProps?: {
    hideSingleValues: boolean;
    getCount: (filter: FilterType) => number;
    onClear?: (filterContext: FilterContextType) => void;
  };
  info?: React.FC;
  about?: React.FC;
  group?: string;
  order?: number;
};

export type filterBoolConfig = filterConfigShared & {
  filterType: filterConfigTypes.OPTIONAL_BOOL;
  facetQuery?: string;
  disableFacetsForSelected?: boolean;
};

export type filterSuggestConfig = filterConfigShared & {
  filterType: filterConfigTypes.SUGGEST;
  facetQuery?: string;
  disableFacetsForSelected?: boolean;
  suggestConfig?: SuggestConfig;
  allowExistence?: boolean;
  allowNegations?: boolean;
  suggestionTitlePath?: string;
};

export type filterTaxonConfig = filterConfigShared & {
  filterType: filterConfigTypes.TAXON;
  facetQuery?: string;
  disableFacetsForSelected?: boolean;
  suggestConfig?: {
    render?: (item: SuggestionItem) => React.ReactNode;
    getSuggestions: (props: TaxonSuggestType) => SuggestResponseType;
    placeholder?: string;
    getStringValue?: (item: SuggestionItem) => string;
  };
  allowExistence?: boolean;
  allowNegations?: boolean;
  suggestionTitlePath?: string;
};

export type filterWildcardConfig = filterConfigShared & {
  filterType: filterConfigTypes.WILDCARD;
  allowExistence?: boolean;
  allowNegations?: boolean;
  queryKey?: string;
  keepCase?: boolean;
  suggestQuery: string;
  disallowLikeFilters?: boolean;
  defaultDescription?: () => React.ReactNode;
};

export type filterEnumConfig = filterConfigShared & {
  filterType: filterConfigTypes.ENUM;
  facetQuery?: string;
  options?: string[];
  allowExistence?: boolean;
  allowNegations?: boolean;
  // Extra GraphQL variables to merge into the facet queries, derived from the current filter.
  // Used e.g. by the targetGene facet to pass the "Similar sequences" matched IDs so the facet
  // correlates to those sequences. Returns {} when nothing extra is needed.
  extraFacetVariables?: (filter: FilterType) => Record<string, unknown>;
};

export type filterRangeConfig = filterConfigShared & {
  filterType: filterConfigTypes.RANGE;
  regex?: RegExp;
  allowExistence?: boolean;
  rangeExample?: () => React.ReactNode;
};

export type filterDateRangeConfig = filterConfigShared & {
  filterType: filterConfigTypes.DATE_RANGE;
  allowExistence?: boolean;
  rangeExample?: () => React.ReactNode;
};

export type filterGeologicalTimeConfig = filterConfigShared & {
  filterType: filterConfigTypes.GEOLOGICAL_TIME;
  allowExistence?: boolean;
};

export type filterLocationConfig = filterConfigShared & {
  filterType: filterConfigTypes.LOCATION;
};

export type filterFreeTextConfig = filterConfigShared & {
  filterType: filterConfigTypes.FREE_TEXT;
};

export type filterInlineToggleConfig = filterConfigShared & {
  filterType: filterConfigTypes.INLINE_TOGGLE;
  options: Array<{ value: string; labelKey: string }>;
};

export type filterHumboldtBooleansConfig = filterConfigShared & {
  filterType: filterConfigTypes.HUMBOLDT_BOOLEANS;
};

export type filterCustomPredicateConfig = filterConfigShared & {
  filterType: filterConfigTypes.CUSTOM_PREDICATE;
};

export type filterSequenceConfig = filterConfigShared & {
  filterType: filterConfigTypes.SEQUENCE;
  // Facet query (on nucleotideSequenceNucleotideSequenceID, with an `include` variable) used
  // to make the identity-bin counts filter-aware under the other active filters.
  facetQuery?: string;
};

// define a type that is one of filterBoolConfig, filterSuggestConfig or filterEnumConfig
export type filterConfig =
  | filterBoolConfig
  | filterSuggestConfig
  | filterEnumConfig
  | filterRangeConfig
  | filterFreeTextConfig
  | filterWildcardConfig
  | filterDateRangeConfig
  | filterTaxonConfig
  | filterGeologicalTimeConfig
  | filterLocationConfig
  | filterInlineToggleConfig
  | filterHumboldtBooleansConfig
  | filterCustomPredicateConfig
  | filterSequenceConfig;

// generic type for a facet query
export interface FacetQuery {
  search: {
    facet?: {
      field?: Array<{
        name: string;
        count: number;
        item?: {
          title?: string | null;
        } | null;
      }>;
    } | null;
  };
}

// generic type for a wildcard queries
export interface WildcardQuery {
  search: {
    cardinality: {
      total: number;
    };
    facet?: {
      field?: Array<{
        name: string;
        count: number;
      }>;
    } | null;
  };
}

export type FacetQueryResponse = {
  data: {
    search: {
      facet: {
        field: {
          name: string;
          count: number;
          item?: {
            title: string;
          };
        }[];
      };
    };
  };
};

function getPopoverFilter({
  Content,
  filterTranslation,
}: {
  Content: React.FC<{
    onApply?: ContentOnApply;
    onCancel?: () => void;
    className?: string;
    style?: React.CSSProperties;
  }>;
  filterTranslation: string;
  className?: string;
}) {
  return function PopoverFilter({
    trigger,
    className,
  }: {
    trigger: React.ReactNode;
    className?: string;
  }) {
    const title = (
      <div className="g-flex g-flex-nowrap g-items-center g-border-b g-p-2 g-px-4 g-min-h-11 sm:g-min-h-7">
        <h3 className="g-flex-auto g-text-slate-800 g-text-sm g-font-semibold">
          <FormattedMessage id={filterTranslation} />
        </h3>
      </div>
    );

    return (
      <FilterPopover trigger={trigger} title={title} className={className}>
        <Content />
      </FilterPopover>
    );
  };
}

const getSuggestFilter = ({
  config,
  searchConfig,
}: {
  config: filterSuggestConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <SuggestFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getTaxonFilter = ({
  config,
  searchConfig,
}: {
  config: filterTaxonConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <TaxonFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getWildcardFilter = ({
  config,
  searchConfig,
}: {
  config: filterWildcardConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <WildcardFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getFreeTextFilter = ({
  config,
  searchConfig,
}: {
  config: filterFreeTextConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <QFilter
          ref={ref}
          filterHandle={config.filterHandle}
          displayName={config.displayName}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getEnumFilter = ({
  config,
  searchConfig,
}: {
  config: filterEnumConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLButtonElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <EnumFilter
          ref={ref}
          options={config.options}
          facetQuery={config.facetQuery}
          filterHandle={config.filterHandle}
          displayName={config.displayName}
          allowExistence={config.allowExistence}
          allowNegations={config.allowNegations}
          extraFacetVariables={config.extraFacetVariables}
          searchConfig={searchConfig}
          about={config.about}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getLocationFilter = ({
  config,
  searchConfig,
}: {
  config: filterLocationConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <GeometryFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getOptionalBooleanFilter = ({
  config,
  searchConfig,
}: {
  config: filterBoolConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<unknown, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <OptionalBooleanFilter
          ref={ref}
          facetQuery={config.facetQuery}
          filterHandle={config.filterHandle}
          displayName={config.displayName}
          searchConfig={searchConfig}
          about={config.about}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getInlineToggleFilter = ({ config }: { config: filterInlineToggleConfig }) => {
  return React.forwardRef<unknown, FilterContentProps>(({ className }, _ref) => (
    <InlineToggleFilter
      className={className}
      filterHandle={config.filterHandle}
      options={config.options}
    />
  ));
};

const getRangeFilter = ({
  config,
  searchConfig,
}: {
  config: filterRangeConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <RangeFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getDateRangeFilter = ({
  config,
  searchConfig,
}: {
  config: filterDateRangeConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <DateRangeFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getGeologicalTimeFilter = ({
  config,
  searchConfig,
}: {
  config: filterGeologicalTimeConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <GeoTimeFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getCustomPredicateFilter = ({ config }: { config: filterCustomPredicateConfig }) => {
  return React.forwardRef<HTMLDivElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <CustomPredicateFilter
          ref={ref}
          filterHandle={config.filterHandle}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getSequenceFilter = ({
  config,
  searchConfig,
}: {
  config: filterSequenceConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLDivElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <SequenceFilter
          ref={ref}
          filterHandle={config.filterHandle}
          about={config.about}
          facetQuery={config.facetQuery}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

const getHumboldtBooleansFilter = ({
  config,
  searchConfig,
}: {
  config: filterHumboldtBooleansConfig;
  searchConfig: FilterConfigType;
}) => {
  return React.forwardRef<HTMLInputElement, FilterContentProps>(
    ({ onApply, onCancel, className, style, pristine }, ref) => {
      return (
        <HumboldtBooleansFilter
          ref={ref}
          {...config}
          searchConfig={searchConfig}
          {...{ onApply, onCancel, className, style, pristine }}
        />
      );
    }
  );
};

export type ContentOnApply = (options?: { keepOpen?: boolean; filter?: FilterType }) => void;

type FilterContentProps = {
  onApply?: ContentOnApply;
  onCancel?: () => void;
  className?: string;
  style?: React.CSSProperties;
  pristine?: boolean;
};

export type FilterSetting = {
  Button: React.FC<{ className?: string; pending?: boolean }>;
  Popover: React.FC<{ trigger: React.ReactNode }>;
  Content: React.FC<{
    onApply?: ContentOnApply;
    onCancel?: () => void;
    ref?: React.ForwardedRef<unknown>;
    className?: string;
    style?: React.CSSProperties;
    pristine?: boolean;
  }>;
  name: string;
  handle: string;
  displayName: React.FC<{ id: string | number | object }>;
  translatedFilterName: string;
  allowNegations?: boolean;
  allowExistence?: boolean;
  filterType: string;
  group?: string;
  order?: number;
};

export type Filters = Record<string, FilterSetting>;

type FilterSettingDefaults = {
  Button: React.FC<{ className?: string }>;
  Popover: React.FC<{ trigger: React.ReactNode }>;
  name: string;
  handle: string;
  displayName: React.FC<{ id: string | number | object }>;
  translatedFilterName: string;
};

type ContentShared = {
  onApply?: ContentOnApply;
  onCancel?: () => void;
  ref: React.ForwardedRef<unknown>;
  className?: string;
  style?: React.CSSProperties;
  pristine?: boolean;
};

export type FilterSettingSuggest = FilterSettingDefaults & {
  Content: React.FC<
    ContentShared & {
      disableFacetsForSelected: boolean;
      facetQuery?: string;
      getSuggestions?: ({ q, intl }: { q: string; intl?: IntlShape }) => Promise<SuggestionItem[]>;
    }
  >;
};

export function generateFilter({
  Content,
  config,
  formatMessage,
  popoverClassName,
}: {
  config: filterConfig;
  formatMessage: IntlShape['formatMessage'];
  Content: React.FC;
  popoverClassName?: string;
}): FilterSetting {
  const PopoverFilter = getPopoverFilter({ Content, filterTranslation: config.filterTranslation });
  let FilterButtonPopover = ({ className, pending }: { className?: string; pending?: boolean }) => {
    return (
      <PopoverFilter
        className={popoverClassName}
        trigger={
          <FilterButton
            className={cn('g-mx-1 g-mb-1 g-max-w-full g-text-slate-600', className)}
            filterHandle={config.filterHandle}
            displayName={config.displayName}
            titleTranslationKey={config.filterTranslation}
            pending={pending}
            {...config.filterButtonProps}
          />
        }
      />
    );
  };
  if (config.filterType === filterConfigTypes.FREE_TEXT) {
    FilterButtonPopover = ({ className }: { className?: string }) => (
      <QInlineButtonFilter className={className} filterHandle={config.filterHandle} />
    );
  }
  if (config.filterType === filterConfigTypes.INLINE_TOGGLE) {
    FilterButtonPopover = ({ className }: { className?: string }) => (
      <InlineToggleFilter
        filterHandle={config.filterHandle}
        options={config.options}
        className={className}
      />
    );
  }

  return {
    // ...config,
    allowExistence: ('allowExistence' in config && config.allowExistence) || false,
    allowNegations: ('allowNegations' in config && config.allowNegations) || false,
    filterType: config.filterType,
    Button: FilterButtonPopover,
    Popover: PopoverFilter,
    Content: Content,
    name: config.filterTranslation,
    handle: config.filterHandle,
    displayName: config.displayName,
    translatedFilterName: formatMessage({ id: config.filterTranslation }),
    group: config.group,
    order: config.order,
  };
}

export function generateFilters({
  config,
  searchConfig,
  formatMessage,
}: {
  config: filterConfig;
  searchConfig: FilterConfigType;
  formatMessage: IntlShape['formatMessage'];
}): FilterSetting {
  if (config.filterType === filterConfigTypes.SUGGEST) {
    return generateFilter({
      config,
      formatMessage,
      Content: getSuggestFilter({ config: config as filterSuggestConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.TAXON) {
    return generateFilter({
      config,
      formatMessage,
      Content: getTaxonFilter({ config: config as filterTaxonConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.ENUM) {
    return generateFilter({
      config,
      formatMessage,
      Content: getEnumFilter({ config: config as filterEnumConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.FREE_TEXT) {
    return generateFilter({
      config,
      formatMessage,
      Content: getFreeTextFilter({ config, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.INLINE_TOGGLE) {
    return generateFilter({
      config,
      formatMessage,
      Content: getInlineToggleFilter({ config }),
    });
  } else if (config.filterType === filterConfigTypes.RANGE) {
    return generateFilter({
      config,
      formatMessage,
      Content: getRangeFilter({ config: config as filterRangeConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.DATE_RANGE) {
    return generateFilter({
      config,
      formatMessage,
      Content: getDateRangeFilter({ config: config as filterDateRangeConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.OPTIONAL_BOOL) {
    return generateFilter({
      config,
      formatMessage,
      Content: getOptionalBooleanFilter({ config: config as filterBoolConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.WILDCARD) {
    return generateFilter({
      config,
      formatMessage,
      Content: getWildcardFilter({ config: config as filterWildcardConfig, searchConfig }),
    });
  } else if (config.filterType === filterConfigTypes.LOCATION) {
    return generateFilter({
      config,
      formatMessage,
      Content: getLocationFilter({ config: config as filterLocationConfig, searchConfig }),
      popoverClassName: 'g-w-[500px] lg:g-w-[700px]',
    });
  } else if (config.filterType === filterConfigTypes.GEOLOGICAL_TIME) {
    return generateFilter({
      config,
      formatMessage,
      Content: getGeologicalTimeFilter({
        config: config as filterGeologicalTimeConfig,
        searchConfig,
      }),
    });
  } else if (config.filterType === filterConfigTypes.HUMBOLDT_BOOLEANS) {
    return generateFilter({
      config,
      formatMessage,
      Content: getHumboldtBooleansFilter({
        config: config as filterHumboldtBooleansConfig,
        searchConfig,
      }),
    });
  } else if (config.filterType === filterConfigTypes.CUSTOM_PREDICATE) {
    return generateFilter({
      config,
      formatMessage,
      Content: getCustomPredicateFilter({ config: config as filterCustomPredicateConfig }),
      popoverClassName: 'g-w-[600px] g-max-w-[var(--radix-popper-available-width)]',
    });
  } else if (config.filterType === filterConfigTypes.SEQUENCE) {
    const setting = generateFilter({
      config,
      formatMessage,
      Content: getSequenceFilter({ config: config as filterSequenceConfig, searchConfig }),
      popoverClassName: 'g-w-[500px] g-max-w-[var(--radix-popper-available-width)]',
    });
    // Feed the chip a "resolving" flag so it shows a spinner while the pasted sequence is being
    // resolved into nucleotideSequenceIDs (published by SequenceResolutionProvider).
    const BaseButton = setting.Button;
    setting.Button = ({ className }) => {
      const { pending } = useSequenceResolution();
      return <BaseButton className={className} pending={pending} />;
    };
    return setting;
  } else {
    // unreachable for typed configs, but configs can come from untyped portal setups
    throw new Error(`Unknown filter type ${(config as filterConfigShared)?.filterType}`);
  }
}

/**
 * Decide which filtesr to show and which to put in a dropdown (and which to remove completely)
 * @param {FilterType} filter - Current filter that the user has selected
 * @param {string[]} existingFilters - A list of all available filters (generally, not for the individual site)
 * @param {string[]} excludedFilters - A list of filters that should not be shown (site owner might have excluded some filters)
 * @param {string[]} highlightedFilters - A list of filters that should always be shown (configured by site owner, default visible filteres)
 * @returns { visibleFilters: string[]; availableFilters: string[] } An object with visibleFilters and availableFilters
 */
export function getFilterConfig({
  currentFilter,
  existingFilters,
  excludedFilters,
  highlightedFilters,
}: {
  currentFilter: FilterType;
  existingFilters: string[]; // a list of filterHandles
  excludedFilters: string[]; // a list of filterHandles
  highlightedFilters: string[]; // a list of filterHandles
}): { visibleFilters: string[]; availableFilters: string[] } {
  const visibleFilters = new Set<string>();
  const highlighted = new Set(highlightedFilters);
  const excluded = new Set(excludedFilters);
  const existing = new Set(existingFilters);
  for (const filter of highlighted) {
    if (!excluded.has(filter)) {
      visibleFilters.add(filter);
    }
  }
  for (const filter of existing) {
    if (currentFilter?.must?.[filter]?.length || currentFilter?.mustNot?.[filter]?.length) {
      visibleFilters.add(filter);
    }
  }
  // get available defined as existing minus excluded
  const availableFilters = new Set(existingFilters.filter((x) => !excludedFilters.includes(x)));

  // if the site is configured to highlight or exlude a filter that do not exist, then log an error to console
  highlightedFilters.forEach((filter) => {
    if (!existingFilters.includes(filter)) {
      console.warn(`Filter ${filter} is configured to be highlighted, but does not exist`);
      console.warn(`Existing filter names are: ${existingFilters.join(', ')}`);
    }
  });
  excludedFilters.forEach((filter) => {
    if (!existingFilters.includes(filter)) {
      console.warn(`Filter ${filter} is configured to be excluded, but does not exist`);
      console.warn(`Existing filter names are: ${existingFilters.join(', ')}`);
    }
  });
  return {
    visibleFilters: Array.from(visibleFilters),
    availableFilters: Array.from(availableFilters),
  };
}

/**
 * Returns a query variable for graphql. A combination of the search scope (e.g. only data from a certain country) + the current user filter. And then transformed to the appropriate field names.
 * It needs a filter. A search context to know the default scope. And it needs to know how to map the filters (which names to use, is it arrays or strings etc)
 * @param {FilterType} filter - Current filter
 * @param {SearchMetadata} searchContext - Context with the underlying scope (e.g. a country or taxon)
 * @param {FilterConfigType} searchConfig - How to map the filters to a query. E.g. how to map it to v1 API or a predicate.
 * @returns {Object} A graphql variable for the query.
 */
// V1 queries carry arbitrary scope and filter params; predicate queries carry the named fields.
export type SearchQuery = {
  predicate?: Predicate;
  q?: string;
  eventFiltering?: EventFiltering;
  [key: string]: ParamValue | ParamValue[];
};

export function getAsQuery({
  filter,
  searchContext,
  searchConfig,
  queryType = searchContext.queryType,
}: {
  filter: FilterType;
  searchContext: SearchMetadata;
  searchConfig: FilterConfigType;
  queryType?: QueryTypeEnum;
}): SearchQuery {
  // should we use get v1 syntax or predicates (we have later added predicates to v1, so the naming is less meaningful now)
  if (queryType === 'V1') {
    const v1Filter = filter2v1(filter, searchConfig);
    const scope = searchContext.scope ?? {};
    // TODO, we could do more to merge here.
    // E.g. the intersection of overlapping keys so you can have a scope of 10 countries but still have the option to search for a country without being able to get something out of scope.
    // But for now we must assume that you cannot search on something that is defined in the root scope
    return { ...scope, ...v1Filter?.filter };
  } else {
    // query by predicate
    // SearchMetadata.scope is untyped; in predicate mode it is a predicate
    const rootPredicate = searchContext.scope as Predicate | undefined;
    let cleanedFilter = filter;

    let q: string | undefined;
    if (searchConfig?.fields?.q?.hoist) {
      q = filter?.must?.q?.[0];
      // remove q from the filter.must and keep the remaining filter as is
      const { q: discard, ...rest } = filter?.must ?? {};
      cleanedFilter = { ...filter, must: rest };
    }

    let eventFiltering: EventFiltering | undefined;
    if (searchConfig?.fields?.eventFiltering?.hoist) {
      eventFiltering =
        filter?.must?.eventFiltering?.[0] === 'past'
          ? EventFiltering.Past
          : EventFiltering.Upcoming;
      // remove eventFiltering from the filter.must and keep the remaining filter as is
      const { eventFiltering: discard, ...rest } = cleanedFilter?.must ?? {};
      cleanedFilter = { ...cleanedFilter, must: rest };
    }

    const currentPredicate = filter2predicate(cleanedFilter, searchConfig);
    const predicates = [rootPredicate, currentPredicate].filter((x): x is Predicate => !!x);
    if (predicates.length === 0) {
      return { q, eventFiltering, predicate: undefined, checklistKey: filter.checklistKey };
    } else if (predicates.length === 1) {
      return { predicate: predicates[0], q, eventFiltering, checklistKey: filter.checklistKey };
    } else {
      return {
        predicate: { type: PredicateType.And, predicates },
        q,
        eventFiltering,
        checklistKey: filter.checklistKey,
      };
    }
  }
}

/**
 * Return filter buttons based on settings. Handles which should be shown, available, hidden, more buttons etc.
 *  @param {FilterType} filter - Current filter
 * @param {SearchMetadata} searchContext - Context with which filters to exclude and highlight
 * @returns
 */
export function FilterButtons({
  filters,
  searchContext,
  groups,
}: {
  filters?: Filters;
  searchContext?: SearchMetadata;
  groups?: string[];
}) {
  const filterContext = useContext(FilterContext);

  // if no filter or an empty object
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }
  if (!filterContext) {
    console.error('FilterContext not found');
    return null;
  }

  const { visibleFilters, availableFilters } = getFilterConfig({
    currentFilter: filterContext.filter,
    existingFilters: Object.keys(filters).map((x) => filters[x]?.handle ?? x),
    excludedFilters: searchContext?.excludedFilters ?? [],
    highlightedFilters: searchContext?.highlightedFilters ?? [],
  });

  // map availableFilters to the form {filterHandle: {Button, Popover, Content}}
  const otherFilters = availableFilters
    .filter((x) => {
      return !visibleFilters.includes(x);
    })
    .reduce((acc, filterHandle) => {
      const filterConfig = filters[filterHandle];
      return { ...acc, [filterHandle]: filterConfig };
    }, {} as Filters);

  return (
    <>
      {visibleFilters?.map((filterHandle) => {
        const filterConfig = filters[filterHandle];
        if (!filterConfig) return null;
        return <filterConfig.Button key={filterHandle} className="g-mx-1 g-mb-1" />;
      })}
      {Object.keys(otherFilters).length > 0 && (
        <MoreFilters filters={otherFilters} groups={groups} />
      )}
    </>
  );
}

export function FilterBar({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('g-py-2 g-px-2 -g-mb-1', className)} role="search">
      {children}
    </div>
  );
}

export function ApplyCancel({
  onApply,
  onCancel,
  pristine,
  disabled,
}: {
  onApply?: (options?: { keepOpen?: boolean }) => void;
  onCancel?: () => void;
  pristine?: boolean;
  disabled?: boolean;
}) {
  if (!onApply || !onCancel) return null;
  return (
    <div className="g-flex-none g-py-3 g-px-4 g-flex g-gap-2 g-justify-between">
      <Button
        size="sm"
        variant="outline"
        onClick={onCancel}
        className="g-flex-none g-h-11 g-text-base g-px-4 sm:g-text-xs sm:g-px-3"
      >
        <FormattedMessage id="filterSupport.cancel" />
      </Button>
      {!pristine && (
        <Button
          type="submit"
          role="button"
          size="sm"
          disabled={disabled}
          onClick={() => onApply({ keepOpen: false })}
          className="g-flex-1 g-h-11 g-text-base g-px-4 sm:g-flex-none sm:g-text-xs sm:g-px-3"
        >
          <FormattedMessage id="filterSupport.apply" />
        </Button>
      )}
    </div>
  );
}

export function AsyncOptions({
  children,
  loading,
  loadingMore,
  error,
  className,
}: {
  children?: React.ReactNode;
  loading: boolean;
  error?: Error | boolean;
  className?: string;
  loadingMore?: boolean;
}) {
  if (error) {
    return (
      <div className="g-p-2 g-m-4 g-text-red-900 g-text-sm">Unable to load suggestions...</div>
    );
  }

  if (loadingMore) {
    return (
      <>
        {children}
        <SkeletonOptions className={className} />;
      </>
    );
  }

  if (loading) {
    return <SkeletonOptions className={className} />;
  }
  return children ?? null;
}

export function SkeletonOptions({ className }: { className?: string }) {
  return (
    <div className={cn(className)}>
      <SkeletonOption className="g-w-full g-mb-2" />
      <SkeletonOption className="g-w-36 g-max-w-full g-mb-2" />
      <SkeletonOption className="g-max-w-full g-w-48 g-mb-2" />
      <SkeletonOption className="g-max-w-full g-w-64 g-mb-2" />
    </div>
  );
}

export type FilterSummaryType = {
  defaultCount: number;
  hasNegations: boolean;
  mixed: boolean;
  isNull: boolean;
  isNotNull: boolean;
  firstValue: { type: string; value: unknown };
};

export function getFilterSummary(filter: FilterType, handle: string): FilterSummaryType {
  const must = filter?.must?.[handle] || [];
  const mustNot = filter?.mustNot?.[handle] || [];

  // check if there is a isNull or isNotNull filter among the filters
  const isNull = must?.[0]?.type === 'isNull' && must.length === 1;
  const isNotNull = must?.[0]?.type === 'isNotNull' && must.length === 1;

  return {
    defaultCount: must.length + mustNot.length,
    hasNegations: mustNot.length > 0,
    mixed: must.length > 0 && mustNot.length > 0,
    isNull,
    isNotNull,
    firstValue: must?.length > 0 ? must?.[0] : mustNot?.[0],
  };
}

export function ExistsSection({
  className,
  backupFilter,
  setFilter,
  setFullField,
  filterHandle,
  About,
  filterSummary,
  onApply,
  onCancel,
  pristine,
}: {
  className?: string;
  backupFilter?: FilterType;
  setFilter: (filter: FilterType) => void;
  setFullField: (field: string, must: unknown[], mustNot: unknown[]) => FilterType;
  filterHandle: string;
  About?: React.FC;
  filterSummary?: FilterSummaryType;
  onApply?: () => void;
  onCancel?: () => void;
  pristine?: boolean;
}) {
  return (
    <>
      <div
        className={cn(
          'g-flex g-flex-none g-text-sm g-text-slate-400 g-py-1.5 g-px-4 g-items-center g-pt-2',
          className
        )}
      >
        <button
          onClick={() => {
            if (backupFilter) setFilter(backupFilter);
            else setFullField(filterHandle, [], []);
          }}
        >
          <FormattedMessage id="filterSupport.backToSelect" />
        </button>

        <div className="g-flex-auto"></div>
        <div className="g-flex-none g-text-base" style={{ marginTop: '-0.2em' }}>
          {About && (
            <AboutButton className="-g-me-1">
              <About />
            </AboutButton>
          )}
        </div>
      </div>
      <div className="g-py-1.5 g-px-4 g-w-full">
        <Exists
          isEmpty={!!filterSummary?.isNull}
          onChange={({ isEmpty }: { isEmpty: boolean }) => {
            if (isEmpty) {
              setFullField(filterHandle, [{ type: 'isNull' }], []);
            } else {
              setFullField(filterHandle, [{ type: 'isNotNull' }], []);
            }
          }}
        />
      </div>
      <ApplyCancel onApply={onApply} onCancel={onCancel} pristine={pristine} />
    </>
  );
}

export function sortFilters(a: FilterSetting, b: FilterSetting) {
  // sort by order f available and else by translated filterName
  const xOrder = a?.order ?? 1000;
  const yOrder = b?.order ?? 1000;
  if (xOrder < yOrder) return -1;
  if (xOrder > yOrder) return 1;

  // sort filters by translatedFilterName
  const xName = a?.translatedFilterName ?? a;
  const yName = b?.translatedFilterName ?? b;
  if (xName < yName) return -1;
  if (xName > yName) return 1;
  return 0;
}
